import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import dayjs from "dayjs";
import Sidebar from "../Sidebar";
import Layout from "../../layout/Layout";
import { GET_LOST_DEMAND } from "./graphql";

// Customers who looked for a slot, found none and left - from the customer
// app's slot events. Tells you when and where to add professionals, and who
// to call back.

const PRESETS = [
  { key: "last7", label: "7 days" },
  { key: "last30", label: "30 days" },
  { key: "last90", label: "90 days" },
];

const OUTCOME = {
  LOST_NO_SLOTS: { label: "No slots at all", cls: "bg-red-50 text-red-700" },
  LOST_PARTLY_FULL: { label: "Wanted day full", cls: "bg-amber-50 text-amber-700" },
  LOST_ERROR: { label: "Slots failed to load", cls: "bg-gray-100 text-gray-700" },
};

const rupees = (v) => (v === null || v === undefined ? "—" : `₹${Math.round(v).toLocaleString("en-IN")}`);
const pct = (a, b) => (b ? `${Math.round((a / b) * 100)}%` : "—");

const Card = ({ title, hint, children }) => (
  <section className="bg-white rounded-xl border border-gray-100 shadow-sm">
    <header className="px-4 pt-4">
      <h2 className="font-semibold text-gray-900">{title}</h2>
      {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
    </header>
    <div className="p-4">{children}</div>
  </section>
);

const Kpi = ({ label, value, sub, alert }) => (
  <div className={`bg-white rounded-xl border p-4 shadow-sm ${alert ? "border-red-200" : "border-gray-100"}`}>
    <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
    <p className={`text-3xl font-semibold mt-1 tabular-nums ${alert ? "text-red-600" : "text-gray-900"}`}>{value}</p>
    {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
  </div>
);

const BarList = ({ rows, label, value, max, color = "bg-red-400", right }) => (
  <div className="space-y-1.5">
    {rows.map((r, i) => (
      <div key={i} className="flex items-center gap-2 text-sm">
        <span className="w-40 shrink-0 truncate text-gray-700" title={label(r)}>{label(r)}</span>
        <span className="flex-1 h-2.5 rounded-full bg-gray-100 overflow-hidden">
          <span className={`block h-full rounded-full ${color}`} style={{ width: `${(value(r) / Math.max(1, max)) * 100}%` }} />
        </span>
        <span className="w-20 text-right tabular-nums text-gray-800">{right ? right(r) : value(r)}</span>
      </div>
    ))}
  </div>
);

const LostDemand = () => {
  const [preset, setPreset] = useState("last30");
  const range = useMemo(() => {
    const days = { last7: 6, last30: 29, last90: 89 }[preset];
    return { startDate: dayjs().subtract(days, "day").format("YYYY-MM-DD"), endDate: dayjs().format("YYYY-MM-DD") };
  }, [preset]);

  const { data, loading, error } = useQuery(GET_LOST_DEMAND, { variables: range, fetchPolicy: "cache-and-network" });
  const res = data?.getLostDemand;
  const d = res?.data;
  const errorMessage = error?.message || (res?.status === "ERROR" && res.message);
  const s = d?.summary;
  const lost = s ? s.lostNoSlots + s.lostPartlyFull + s.lostError : 0;
  const capacityAsked = s ? lost + s.recovered + s.booked : 0;

  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <Layout>
        <div className="max-w-7xl space-y-5 pb-16">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="text-2xl font-semibold text-white">Lost demand</h1>
              <p className="text-sm text-gray-400 mt-0.5">
                Customers who looked for a slot in the app, found none and didn't book within {d?.bookWindowHours || 48} hours.
              </p>
            </div>
            <div className="flex rounded-lg bg-white/10 p-0.5">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPreset(p.key)}
                  className={`px-3 py-1.5 rounded-md text-sm ${preset === p.key ? "bg-white font-semibold text-gray-900" : "text-gray-300 hover:text-white"}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {errorMessage && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">Could not load lost demand: {errorMessage}</div>}
          {loading && !d && <div className="py-16 text-center text-gray-400">Loading…</div>}

          {d && s && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <Kpi label="Lost customers" value={lost} sub={`${pct(lost, capacityAsked)} of people who hit a full day or booked`} alert={lost > 0} />
                <Kpi label="Value in their carts" value={rupees(s.lostValue)} sub="where the cart value is known" alert={s.lostValue > 0} />
                <Kpi label="No slots at all" value={s.lostNoSlots} sub="every date they tried was full" />
                <Kpi label="Wanted day full" value={s.lostPartlyFull} sub="other days had slots, still left" />
                <Kpi label="Found full, booked anyway" value={s.recovered} sub={`of ${s.searches} searches · ${s.booked} booked first try`} />
              </div>

              <div className="grid lg:grid-cols-3 gap-5">
                <Card title="Dates people wanted and found full" hint="The booking date asked for. Add professionals or open slots here first.">
                  {d.wantedDates.length ? (
                    <BarList
                      rows={d.wantedDates.slice(0, 12)}
                      label={(r) => `${dayjs(r.wantedDate).format("ddd D MMM")}${r.female ? ` · ${r.female} women's` : ""}`}
                      value={(r) => r.lost}
                      max={Math.max(...d.wantedDates.map((r) => r.lost))}
                      right={(r) => `${r.lost} lost`}
                    />
                  ) : (
                    <p className="text-sm text-gray-400">Nobody hit a full day.</p>
                  )}
                </Card>
                <Card title="Where" hint="~1 km areas from the customer's map pin. Click to open the map.">
                  {d.areas.length ? (
                    <div className="space-y-1.5">
                      {d.areas.map((a, i) => (
                        <a
                          key={i}
                          href={`https://www.google.com/maps?q=${a.lat},${a.lng}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center justify-between gap-2 text-sm hover:bg-gray-50 rounded px-1"
                        >
                          <span className="truncate text-gray-700" title={a.sampleAddress || ""}>{a.sampleAddress || `${a.lat.toFixed(2)}, ${a.lng.toFixed(2)}`}</span>
                          <span className="shrink-0 tabular-nums text-gray-800">
                            {a.lost} <span className="text-xs text-gray-400">{a.lostValue ? rupees(a.lostValue) : ""}</span>
                          </span>
                        </a>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">No locations recorded for lost searches.</p>
                  )}
                </Card>
                <Card title="Services they wanted" hint="From the cart at the time.">
                  {d.services.length ? (
                    <BarList rows={d.services} label={(r) => r.serviceName} value={(r) => r.lost} max={Math.max(...d.services.map((r) => r.lost))} color="bg-amber-400" />
                  ) : (
                    <p className="text-sm text-gray-400">Not recorded.</p>
                  )}
                </Card>
              </div>

              <Card title="Day by day" hint="When the searching happened. Bar = searches; red = lost.">
                <div className="overflow-x-auto">
                  <div className="flex items-end gap-1 h-36 min-w-max">
                    {d.byDay.map((x) => {
                      const max = Math.max(1, ...d.byDay.map((y) => y.searches));
                      return (
                        <div key={x.day} className="flex flex-col items-center justify-end h-full w-6" title={`${dayjs(x.day).format("D MMM")}: ${x.searches} searches, ${x.booked} booked, ${x.lost} lost ${x.lostValue ? rupees(x.lostValue) : ""}`}>
                          <div className="w-4 flex flex-col justify-end rounded-t overflow-hidden bg-gray-200" style={{ height: `${(x.searches / max) * 100}%` }}>
                            <div className="bg-red-500" style={{ height: `${(x.lost / Math.max(1, x.searches)) * 100}%` }} />
                          </div>
                          <span className="text-[9px] text-gray-500 mt-1">{dayjs(x.day).format("D")}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </Card>

              <Card title="Customers to call back" hint="Logged-in customers have a name and number. Biggest carts first (up to 100).">
                <div className="overflow-x-auto -mx-4">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                        {["Searched", "Customer", "What happened", "Dates full", "Cart", "Services", "Address"].map((h) => (
                          <th key={h} className="px-3 py-2 font-medium whitespace-nowrap first:pl-4">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {d.searches.map((x) => {
                        const o = OUTCOME[x.outcome] || { label: x.outcome, cls: "bg-gray-100 text-gray-600" };
                        return (
                          <tr key={`${x.who}-${x.searchDay}`} className="border-b border-gray-50 align-top">
                            <td className="px-3 pl-4 py-2 whitespace-nowrap">{x.firstAt ? dayjs(x.firstAt).format("D MMM, h:mm A") : x.searchDay}</td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {x.customerName || (x.userId ? "Customer" : <span className="text-gray-400">Not logged in</span>)}
                              {x.phone && (
                                <a href={`tel:${x.phone}`} className="block text-xs text-gray-500 hover:text-gray-900">
                                  {x.phone}
                                </a>
                              )}
                            </td>
                            <td className="px-3 py-2"><span className={`px-1.5 py-0.5 rounded text-xs whitespace-nowrap ${o.cls}`}>{o.label}</span></td>
                            <td className="px-3 py-2 text-xs text-gray-700 whitespace-nowrap">{x.emptyDates.map((e) => dayjs(e).format("D MMM")).join(", ") || "—"}</td>
                            <td className="px-3 py-2 font-semibold tabular-nums">{rupees(x.cartValue)}</td>
                            <td className="px-3 py-2 text-xs text-gray-600 max-w-[220px]">{x.serviceNames.join(", ") || "—"}</td>
                            <td className="px-3 py-2 text-xs text-gray-500 max-w-[200px] truncate" title={x.address || ""}>{x.address || "—"}</td>
                          </tr>
                        );
                      })}
                      {!d.searches.length && (
                        <tr>
                          <td colSpan={7} className="px-4 py-8 text-center text-gray-400">Nobody was lost to full slots in this period.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}
        </div>
      </Layout>
    </div>
  );
};

export default LostDemand;
